import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase/config';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { logger } from '../utils/logger';
import Step5Certifications from '../components/OnBoarding/Step5Certifications';

interface Certification {
  certificationId: string;
  date: string;
  fileUrl: string;
  issuer: string;
  name: string;
  uploadedAt: string;
  frontImageUrl?: string;
  backImageUrl?: string;
  status?: string;
}

const MyCertifications: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState<{ open: boolean, certification?: Certification | null }>({ open: false, certification: null });
  const db = getFirestore();
  const storage = getStorage();

  useEffect(() => {
    const loadCertifications = async () => {
      try {
        if (!auth.currentUser) {
          logger.error('MyCertifications', 'No hay usuario autenticado');
          return;
        }

        const userId = auth.currentUser.uid;
        logger.info('MyCertifications', 'Cargando certificaciones para usuario:', userId);

        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) {
          logger.error('MyCertifications', 'Documento de usuario no encontrado');
          return;
        }

        const userData = userDoc.data();
        logger.info('MyCertifications', 'Datos del usuario:', userData);

        if (userData.certifications && Array.isArray(userData.certifications)) {
          logger.info('MyCertifications', 'Certificaciones encontradas:', userData.certifications);
          const processedCertifications = userData.certifications.map((cert: any) => ({
            ...cert,
            status: cert['cert-status'] || 'pending'
          }));
          setCertifications(processedCertifications);
        } else {
          logger.info('MyCertifications', 'No se encontraron certificaciones');
          setCertifications([]);
        }
      } catch (error) {
        logger.error('MyCertifications', 'Error al cargar certificaciones', error);
        setError(t('myCertifications.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    loadCertifications();
  }, [t]);

  const handleEdit = (certificationId: string) => {
    const cert = certifications.find(c => c.certificationId === certificationId);
    if (cert) {
      setShowModal({ open: true, certification: cert });
    }
  };

  const handleDeleteCertification = async (certificationId: string) => {
    if (window.confirm(t('common.confirmDelete'))) {
      try {
        setLoading(true);
        logger.info('MyCertifications', 'Eliminando certificación:', certificationId);
        logger.info('MyCertifications', 'Total de certificaciones antes de eliminar:', certifications.length);
        
        if (!auth.currentUser) {
          throw new Error('Usuario no autenticado');
        }

        const userId = auth.currentUser.uid;
        
        // Encontrar la certificación a eliminar
        const certificationToDelete = certifications.find(cert => cert.certificationId === certificationId);
        if (!certificationToDelete) {
          logger.error('MyCertifications', 'Certificación no encontrada con ID:', certificationId);
          logger.info('MyCertifications', 'Certificaciones disponibles:', certifications.map(c => ({ id: c.certificationId, name: c.name })));
          throw new Error('Certificación no encontrada');
        }

        logger.info('MyCertifications', 'Certificación encontrada para eliminar:', {
          id: certificationToDelete.certificationId,
          name: certificationToDelete.name
        });

        // Eliminar archivos de Firebase Storage si existen
        const filesToDelete = [];
        
        if (certificationToDelete.fileUrl) {
          filesToDelete.push(certificationToDelete.fileUrl);
        }
        if (certificationToDelete.frontImageUrl) {
          filesToDelete.push(certificationToDelete.frontImageUrl);
        }
        if (certificationToDelete.backImageUrl) {
          filesToDelete.push(certificationToDelete.backImageUrl);
        }

        // Eliminar archivos de Storage
        if (filesToDelete.length > 0) {
          try {
            for (const fileUrl of filesToDelete) {
              try {
                // Extraer la ruta del archivo de la URL
                const urlParts = fileUrl.split('/o/');
                if (urlParts.length > 1) {
                  const encodedPath = urlParts[1].split('?')[0];
                  const decodedPath = decodeURIComponent(encodedPath);
                  const fileRef = ref(storage, decodedPath);
                  await deleteObject(fileRef);
                  logger.info('MyCertifications', 'Archivo eliminado de Storage:', decodedPath);
                }
              } catch (storageError) {
                logger.warn('MyCertifications', 'No se pudo eliminar archivo de Storage:', fileUrl);
                // Continuar con la eliminación aunque falle el storage
              }
            }
          } catch (storageError) {
            logger.warn('MyCertifications', 'Error al eliminar archivos de Storage:', storageError instanceof Error ? storageError.message : String(storageError));
            // Continuar con la eliminación aunque falle el storage
          }
        }

        // Eliminar la certificación del array en Firestore
        const updatedCertifications = certifications.filter(cert => {
          const matches = cert.certificationId !== certificationId;
          console.log(`🔍 Comparando: "${cert.certificationId}" !== "${certificationId}" = ${matches}`);
          return matches;
        });
        
        // DEBUG: Verificar que solo se elimine la certificación específica
        console.log('🔍 DEBUG ELIMINACIÓN:');
        console.log('ID a eliminar:', certificationId);
        console.log('Certificaciones antes:', certifications.map(c => ({ id: c.certificationId, name: c.name })));
        console.log('Certificaciones después:', updatedCertifications.map(c => ({ id: c.certificationId, name: c.name })));
        console.log('Total antes:', certifications.length);
        console.log('Total después:', updatedCertifications.length);
        
        logger.info('MyCertifications', 'Certificaciones después del filtrado:', updatedCertifications.length);
        logger.info('MyCertifications', 'Certificación eliminada del array local');
        
        await updateDoc(doc(db, 'users', userId), {
          certifications: updatedCertifications,
          updatedAt: new Date().toISOString()
        });

        // Actualizar el estado local
        setCertifications(updatedCertifications);
        
        logger.info('MyCertifications', 'Certificación eliminada exitosamente del Firestore');
        
        // Mostrar mensaje de éxito
        alert(t('myCertifications.certificationDeleted') || 'Certificación eliminada exitosamente');
        
      } catch (error) {
        logger.error('MyCertifications', 'Error al eliminar certificación:', error instanceof Error ? error.message : String(error));
        setError(t('common.errorDeleting'));
        
        // Mostrar mensaje de error
        alert(t('common.errorDeleting') || 'Error al eliminar la certificación');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] flex items-center justify-center">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] flex items-center justify-center">
        <div className="bg-white border border-red-200 rounded-2xl p-8 shadow-2xl max-w-md animate-fadeIn">
          <p className="text-red-700 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-[20px_40px_20px_40px] shadow-2xl p-8 mb-8 border border-white/40 animate-fadeIn">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h1 className="text-3xl font-bold text-red-600">{t('onboarding.myCertifications.title')}</h1>
            <div className="flex gap-2">
              <button 
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors duration-200 flex items-center gap-2 shadow-lg"
                onClick={() => setShowModal({ open: true, certification: null })}
              >
                <FaPlus /> {t('onboarding.myCertifications.addCertification')}
              </button>
            </div>
          </div>
        </div>

        {showModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-3xl w-full relative overflow-y-auto" style={{ maxHeight: '90vh' }}>
              <button className="absolute top-3 right-4 text-2xl text-gray-400 hover:text-red-600" onClick={() => setShowModal({ open: false, certification: null })}>&times;</button>
              <Step5Certifications 
                onComplete={(action) => {
                  setShowModal({ open: false, certification: null });
                  if (action === 'reload') {
                    // Recargar certificaciones
                    (async () => {
                      if (!auth.currentUser) return;
                      const userId = auth.currentUser.uid;
                      const userDoc = await getDoc(doc(db, 'users', userId));
                      if (userDoc.exists()) {
                        const userData = userDoc.data();
                        setCertifications(userData.certifications || []);
                      }
                    })();
                  }
                }}
                initialCertifications={showModal.certification ? [{
                  id: showModal.certification.certificationId,
                  certificationId: showModal.certification.certificationId,
                  date: showModal.certification.date,
                  file: null,
                  preview: null,
                  frontImage: null,
                  frontPreview: null,
                  backImage: null,
                  backPreview: null,
                  fileUrl: showModal.certification.fileUrl || null,
                  frontImageUrl: (showModal.certification as any).frontImageUrl || null,
                  backImageUrl: (showModal.certification as any).backImageUrl || null
                }] : []}
                isEditMode={!!showModal.certification}
                existingCertifications={certifications}
              />
            </div>
          </div>
        )}

        {certifications.length === 0 ? (
          <div className="bg-white rounded-[20px_40px_20px_40px] shadow-2xl p-12 text-center border border-white/40 animate-fadeIn">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaPlus className="text-3xl text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('myCertifications.noCertifications')}</h3>
              <p className="text-gray-600 mb-6">{t('myCertifications.startText')}</p>
              <button 
                className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl font-semibold transition-colors duration-200 shadow-lg"
                onClick={() => navigate('/add-certification')}
              >
                {t('myCertifications.addFirstCertification')}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert) => (
              <div key={cert.certificationId} className="bg-white rounded-[20px_40px_20px_40px] shadow-2xl p-6 hover:shadow-xl transition-shadow duration-200 border border-white/40 animate-fadeIn flex flex-col justify-between">
                {/* Header */}
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{cert.name}</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
                      {cert.issuer}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      cert.status === 'approved' ? 'bg-green-100 text-green-800' :
                      cert.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {cert.status === 'approved' ? (i18n.language === 'es' ? 'Aprobada' : 'Approved') :
                       cert.status === 'rejected' ? (i18n.language === 'es' ? 'Rechazada' : 'Rejected') :
                       (i18n.language === 'es' ? 'Pendiente' : 'Pending')}
                    </span>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">{t('onboarding.myCertifications.date')}:</span>
                    <span className="text-sm text-gray-900">
                      {format(new Date(cert.date), 'PPP', { locale: i18n.language === 'es' ? es : enUS })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">{t('onboarding.myCertifications.uploadedAt')}:</span>
                    <span className="text-sm text-gray-900">
                      {format(new Date(cert.uploadedAt), 'PPP', { locale: i18n.language === 'es' ? es : enUS })}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button 
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                    onClick={() => handleEdit(cert.certificationId)}
                    title={t('common.edit')}
                  >
                    <FaEdit className="text-sm" />
                    {t('common.edit')}
                  </button>
                  <button 
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl font-medium transition-colors duration-200 flex items-center justify-center"
                    onClick={() => handleDeleteCertification(cert.certificationId)}
                    title={t('common.delete')}
                  >
                    <FaTrash className="text-sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCertifications; 